import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import axios from "axios";
import { baseURL } from "../../API/API";
import Header from "../../Components/common/Header";
import Footer from "../../Components/common/Footer";
import useAuth from "../hooks/useAuth";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import BundleContentAccordion from "./BundleContentAccordion";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faCoins, faUnlock } from "@fortawesome/free-solid-svg-icons";

export default function ComboDetail() {
  const { comboId } = useParams();
  const navigate = useNavigate();
  const { auth } = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const [combo, setCombo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const client = auth?.accessToken ? axiosPrivate : axios;
    client.get(`${baseURL}/learning/combos/detail/${comboId}`)
      .then((res) => setCombo(res.data?.data))
      .catch((err) => {
        console.error("Error fetching combo details:", err);
        toast.error("فشل تحميل تفاصيل الباقة");
      })
      .finally(() => setLoading(false));
  }, [auth?.accessToken, axiosPrivate, comboId]);

  if (loading) {
    return <><Header /><div className="text-center py-5 text-muted"><h3>جاري تحميل تفاصيل الباقة...</h3></div><Footer /></>;
  }

  if (!combo) {
    return <><Header /><div className="text-center py-5 text-muted"><h3>الباقة غير موجودة</h3></div><Footer /></>;
  }

  const totalPrice = Number(combo.price || 0);
  const discountVal = Number(combo.discount || 0);
  const finalPrice = Math.max(totalPrice - discountVal, 0);
  const chapterNames = combo.chapters ? combo.chapters.map((c) => c.name).join(" و ") : "";
  const autoDescription = `هذه باقة تعليمية مميزة تجمع بين عدة وحدات وشهور دراسية مشمولة معا (${chapterNames})، تم إعدادها خصيصا لتمنحك تجربة دراسية متكاملة ومستمرة مع نخبة من معلمي الأكاديمية وبخصم خاص عند الاشتراك الموحد.`;

  const handleSubscribeClick = () => {
    if (!auth?.accessToken) {
      toast.info("يرجى إنشاء حساب أو تسجيل الدخول لعرض الفاتورة والاشتراك");
      navigate("/register");
      return;
    }
    navigate(`/invoice/combo/${combo.id}`);
  };

  return (
    <div className="ComboDetailPage" dir="rtl" style={{ backgroundColor: "#f9fafb", minHeight: "100vh" }}>
      <Header />
      <div className="learn-purple-banner py-5" style={{ background: "linear-gradient(135deg, #4c0d16 0%, #7c3aed 100%)", color: "#fff" }}>
        <Container>
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-4">
            <div className="banner-content text-end">
              <span className="badge bg-warning text-dark mb-3 px-3 py-2 fs-6">باقة كورسات مشتركة (كومبو)</span>
              <h1 className="banner-title fw-bold mb-3">{combo.name}</h1>
              <p className="banner-desc mb-2 opacity-90" style={{ maxWidth: "800px", fontSize: "1.05rem", lineHeight: "1.7" }}>
                {combo.description || autoDescription}
              </p>
            </div>
            <Link to="/" className="btn btn-banner-back d-flex align-items-center gap-2 text-decoration-none text-white px-4 py-2 rounded-pill" style={{ border: "1.5px solid #fff" }}>
              <span>العودة للرئيسية</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          </div>
        </Container>
      </div>

      <Container className="my-5">
        <Row className="g-4">
          <Col lg={8}>
            <BundleContentAccordion
              chapters={combo.chapters || []}
              hasAccess={combo.has_accessed}
              title="شهور ودروس الكومبو"
              returnTo={`/combos/${combo.id}`}
              returnLabel="العودة للكومبو"
            />

            <Card className="border shadow-sm rounded-4 p-4 text-end">
              <h4 className="fw-bold text-dark mb-3">وصف وتفاصيل الباقة المشتركة</h4>
              <p className="text-muted" style={{ lineHeight: "1.8", fontSize: "1rem" }}>{autoDescription}</p>
            </Card>
          </Col>

          <Col lg={4}>
            <Card className="border shadow-sm rounded-4 p-4 text-center sticky-top" style={{ top: "100px", zIndex: 10 }}>
              {combo.image && (
                <div className="mb-4 rounded-3 overflow-hidden" style={{ height: "200px" }}>
                  <img src={combo.image} alt={combo.name} className="w-100 h-100 object-fit-cover" />
                </div>
              )}

              <h4 className="fw-bold text-dark mb-4">تفاصيل الاشتراك بالباقة</h4>
              <div className="bg-light p-4 rounded-4 mb-4">
                <div className="text-muted small mb-2">القيمة الإجمالية للوحدات منفردة:</div>
                <div className="text-decoration-line-through text-muted mb-3 fs-5">
                  {(combo.chapters ? combo.chapters.reduce((sum, c) => sum + Number(c.price || 0), 0) : 0).toFixed(0)} جنيه مصري
                </div>
                <div className="text-muted small mb-1">سعر الكومبو الموحد بعد الخصم:</div>
                <div className="fs-2 fw-bold text-primary mb-2">{finalPrice.toFixed(0)} جنيه</div>
                {discountVal > 0 && <span className="badge bg-danger-light text-danger px-3 py-2 rounded-pill small">خصم باقة الكومبو: {discountVal.toFixed(0)} جنيه</span>}
              </div>

              {combo.has_accessed ? (
                <div className="alert alert-success d-flex align-items-center justify-content-center gap-2 rounded-pill mb-0">
                  <FontAwesomeIcon icon={faUnlock} />
                  <span className="fw-bold">أنت مشترك بالفعل، افتح الشهور هنا وابدأ الدرس من صفحة الدرس</span>
                </div>
              ) : (
                <Button onClick={handleSubscribeClick} className="w-100 rounded-pill py-3 fw-bold fs-5 flex-center-both gap-2" style={{ backgroundColor: "var(--brand-300)", borderColor: "var(--brand-300)", color: "#fff" }}>
                  <FontAwesomeIcon icon={faCoins} />
                  <span>اشترك الآن في الباقة!</span>
                </Button>
              )}
            </Card>
          </Col>
        </Row>
      </Container>
      <Footer />
    </div>
  );
}